import React, { useEffect, useState, useCallback, useRef } from "react";
import { api, fmtDateTime, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { MessageCircle, Send, CheckCircle2, Circle, Trash2, Users, ChevronRight } from "lucide-react";

function timeSince(iso) {
  if (!iso) return "";
  const diff = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return fmtDateTime(iso);
}

function Avatar({ name, size = 8 }) {
  const initials = (name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const colors = ["bg-blue-500","bg-purple-500","bg-green-500","bg-amber-500","bg-red-500","bg-pink-500"];
  const color = colors[name?.charCodeAt(0) % colors.length || 0];
  return (
    <div className={`w-${size} h-${size} rounded-full ${color} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
      {initials}
    </div>
  );
}

export function TeamChatPage() {
  const { auth } = useAuth();
  const myId = auth?.user?.id;

  const [users, setUsers]     = useState([]);
  const [threads, setThreads] = useState([]);
  const [activeUid, setActiveUid] = useState(null);
  const [messages, setMessages]   = useState([]);
  const [otherUser, setOtherUser] = useState(null);
  const [text, setText]           = useState("");
  const [isTask, setIsTask]       = useState(false);
  const [taskTitle, setTaskTitle] = useState("");
  const [sending, setSending]     = useState(false);
  const [error, setError]         = useState("");
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const bottomRef = useRef(null);

  const loadUsers = useCallback(async () => {
    try {
      const r = await api.get("/chat/users");
      setUsers((r.data || []).filter(u => !u.is_self));
    } catch (err) { setError(formatApiError(err)); }
  }, []);

  const loadThreads = useCallback(async () => {
    try {
      const r = await api.get("/chat/threads");
      setThreads(r.data || []);
    } catch (err) { /* ignore */ }
  }, []);

  const loadMessages = useCallback(async (uid) => {
    if (!uid) return;
    setLoadingMsgs(true);
    try {
      const r = await api.get(`/chat/messages?with_user_id=${uid}`);
      setMessages(r.data?.messages || []);
      setOtherUser(r.data?.other || null);
      await loadThreads();
    } catch (err) { setError(formatApiError(err)); }
    finally { setLoadingMsgs(false); }
  }, [loadThreads]);

  useEffect(() => {
    loadUsers();
    loadThreads();
  }, [loadUsers, loadThreads]);

  useEffect(() => {
    if (activeUid) loadMessages(activeUid);
  }, [activeUid, loadMessages]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Poll for new messages every 5s
  useEffect(() => {
    if (!activeUid) return;
    const interval = setInterval(() => loadMessages(activeUid), 5000);
    return () => clearInterval(interval);
  }, [activeUid, loadMessages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !isTask) return;
    setSending(true);
    try {
      await api.post("/chat/messages", {
        to_user_id: activeUid, text: text.trim(),
        is_task: isTask, task_title: isTask ? (taskTitle || text) : null,
      });
      setText(""); setTaskTitle(""); setIsTask(false);
      await loadMessages(activeUid);
    } catch (err) { setError(formatApiError(err)); }
    finally { setSending(false); }
  };

  const markTaskDone = async (msgId) => {
    try {
      await api.patch(`/chat/messages/${msgId}/task-done`);
      await loadMessages(activeUid);
    } catch (err) { setError(formatApiError(err)); }
  };

  const deleteMsg = async (msgId) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/chat/messages/${msgId}`);
      setMessages(prev => prev.filter(m => m.id !== msgId));
    } catch (err) { setError(formatApiError(err)); }
  };

  // Merge users with thread data for display
  const allContacts = users.map(u => {
    const thread = threads.find(t =>
      t.participants?.some(p => p.id === u.id)
    );
    return { ...u, lastMsg: thread?.last_message?.text || "", lastAt: thread?.last_at || "",
             unread: thread?.unread_count || 0 };
  }).sort((a, b) => (b.lastAt || "").localeCompare(a.lastAt || ""));

  return (
    <div className="flex h-[calc(100vh-80px)] overflow-hidden" data-testid="team-chat-page">
      {/* Left: Contact list */}
      <div className="w-72 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col">
        <div className="px-4 py-4 border-b border-gray-100">
          <h1 className="font-black text-xl tracking-tight flex items-center gap-2">
            <MessageCircle size={18} /> Team Chat
          </h1>
          <p className="text-xs text-gray-400 mt-0.5">{users.length} team member{users.length !== 1 ? "s" : ""}</p>
        </div>
        {error && (
          <div className="mx-3 mt-2 p-2 bg-red-50 text-red-700 text-xs rounded">{error}
            <button onClick={() => setError("")} className="ml-2 font-bold">×</button>
          </div>
        )}
        <div className="overflow-y-auto flex-1">
          {allContacts.length === 0 ? (
            <div className="p-4 text-sm text-gray-400 text-center py-8">
              <Users size={24} className="mx-auto mb-2 text-gray-300" />
              No other team members yet
            </div>
          ) : allContacts.map(u => (
            <button key={u.id} onClick={() => setActiveUid(u.id)}
              className={`w-full text-left flex items-center gap-3 px-4 py-3 border-b border-gray-50 transition-colors
                ${activeUid === u.id ? "bg-gray-900 text-white" : "hover:bg-gray-50 text-gray-800"}`}
              data-testid={`chat-contact-${u.id}`}>
              <Avatar name={u.name} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-sm truncate">{u.name}</span>
                  {u.unread > 0 && (
                    <span className="ml-2 bg-blue-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center flex-shrink-0">
                      {u.unread}
                    </span>
                  )}
                </div>
                <div className={`text-xs truncate mt-0.5 ${activeUid === u.id ? "text-gray-300" : "text-gray-400"}`}>
                  {u.lastMsg || <span className="italic">{u.role}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right: Message thread */}
      <div className="flex-1 flex flex-col bg-gray-50 min-w-0">
        {!activeUid ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle size={40} className="mx-auto mb-3 text-gray-200" />
              <div className="text-gray-400 text-sm font-medium">Select a person to chat</div>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-5 py-3 bg-white border-b border-gray-200 flex items-center gap-3">
              {otherUser && <Avatar name={otherUser.name} />}
              <div>
                <div className="font-bold text-gray-900">{otherUser?.name || "..."}</div>
                <div className="text-xs text-gray-400 capitalize">{otherUser?.role || ""}</div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              {loadingMsgs ? (
                <div className="text-center text-sm text-gray-400 py-8">Loading messages…</div>
              ) : messages.length === 0 ? (
                <div className="text-center text-sm text-gray-400 py-8">No messages yet. Say hi!</div>
              ) : messages.map(msg => {
                const isMine = msg.from_user_id === myId;
                return (
                  <div key={msg.id} className={`flex items-end gap-2 ${isMine ? "justify-end" : "justify-start"}`}>
                    {!isMine && <Avatar name={msg.from_name} size={7} />}
                    <div className={`max-w-[65%] group`}>
                      {/* Task card */}
                      {msg.is_task && msg.task ? (
                        <div className={`rounded-xl border p-3 text-sm ${isMine ? "bg-white border-gray-200" : "bg-white border-gray-200"}`}>
                          <div className="flex items-center gap-2 mb-1">
                            {msg.task.status === "done"
                              ? <CheckCircle2 size={14} className="text-green-500" />
                              : <Circle size={14} className="text-amber-500" />}
                            <span className="font-bold text-gray-800 text-xs uppercase tracking-wide">Task</span>
                            {msg.task.priority !== "normal" && (
                              <span className="text-xs bg-red-100 text-red-700 px-1.5 rounded">{msg.task.priority}</span>
                            )}
                          </div>
                          <div className="font-semibold text-gray-800">{msg.task.text}</div>
                          {msg.task.due_date && <div className="text-xs text-gray-400 mt-1">Due: {msg.task.due_date}</div>}
                          {msg.task.status === "done"
                            ? <div className="text-xs text-green-600 mt-1">Done by {msg.task.done_by}</div>
                            : !isMine && (
                              <button onClick={() => markTaskDone(msg.id)}
                                className="mt-2 text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded-lg border border-green-200 hover:bg-green-100 font-semibold">
                                Mark Done
                              </button>
                            )}
                        </div>
                      ) : (
                        <div className={`rounded-2xl px-3.5 py-2 text-sm ${isMine ? "bg-gray-900 text-white rounded-br-sm" : "bg-white text-gray-800 border border-gray-200 rounded-bl-sm"}`}>
                          {msg.text}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-1 px-1">
                        <span className="text-xs text-gray-400">{timeSince(msg.created_at)}</span>
                        <button onClick={() => deleteMsg(msg.id)}
                          className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all">
                          <Trash2 size={11} />
                        </button>
                      </div>
                    </div>
                    {isMine && <Avatar name={msg.from_name} size={7} />}
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            {/* Input area */}
            <div className="bg-white border-t border-gray-200 px-4 py-3">
              {isTask && (
                <div className="mb-2">
                  <input value={taskTitle} onChange={e => setTaskTitle(e.target.value)} placeholder="Task title…"
                    className="w-full border border-amber-300 bg-amber-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-500" />
                </div>
              )}
              <form onSubmit={send} className="flex items-center gap-2">
                <button type="button" onClick={() => setIsTask(!isTask)}
                  className={`flex-shrink-0 p-2 rounded-lg border text-xs font-semibold transition-colors ${isTask ? "bg-amber-500 text-white border-amber-500" : "text-gray-400 border-gray-200 hover:bg-gray-50"}`}
                  title={isTask ? "Cancel task" : "Send as task"}>
                  ✓ Task
                </button>
                <input value={text} onChange={e => setText(e.target.value)} placeholder={isTask ? "Message (optional)…" : "Type a message…"}
                  className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-blue-500"
                  data-testid="chat-message-input" />
                <button type="submit" disabled={sending || (!text.trim() && !isTask)}
                  className="flex-shrink-0 w-10 h-10 bg-gray-900 text-white rounded-xl flex items-center justify-center hover:bg-gray-700 disabled:opacity-40 transition-colors"
                  data-testid="send-message-btn">
                  <Send size={16} />
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default TeamChatPage;
